<template>
  <div>
    <RevContainer>
      <div v-if="shouldShowContent" class="py-24">
        <InsuranceSelection
          v-if="cartItem"
          :insurance-offers="cartItem.insuranceOffers"
          :listing-id="cartItem.listingId"
          :model="cartItem.model"
          :selected-offer-id="selectedOffer?.id"
          tracking-zone="insurance_atc"
          :with-link="false"
          @remove="removeInsurance"
          @select="addInsurance"
        />

        <div ref="compliancyElement">
          <InsuranceCompliancy
            v-if="selectedOffer"
            id="atc-overlay-insurance-compliancy"
            class="mt-16"
            :disabled="false"
            :offer="selectedOffer"
            @update="toggleCompliancy"
          />

          <p v-if="selectedOffer && isError" class="text-static-danger-hi mt-3">
            {{ i18n(translations.compliancyError) }}
          </p>
        </div>
      </div>

      <!-- Adding some bottom padding to avoid content being hidden by the sticky bar -->
      <div v-if="shouldShowContent" class="py-24 pb-96">
        <RevDivider class="p-24" />
        <BackupDrawerFaq v-if="!hasBackupPlusOffers" />
        <BackupPlusDrawerFaq v-else />
      </div>

      <RevStickyBar class="flex justify-between px-24 py-12">
        <RevLink data-qa="atc-insurances-back" @click="handleBack">
          {{ i18n(translations.back) }}
        </RevLink>
        <RevButton
          data-qa="atc-insurances-submit"
          :loading="isLoading"
          variant="primary"
          @click="handleSubmit"
        >
          {{
            i18n(
              selectedOffer
                ? translations.addProtection
                : translations.skipProtection,
            )
          }}
        </RevButton>
      </RevStickyBar>
    </RevContainer>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData } from '#imports'
import { computed, ref, useTemplateRef, watch } from 'vue'

import type { InsuranceOffer } from '@backmarket/http-api'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'
import { RevStickyBar } from '@ds/components/StickyBar'

import InsuranceSelection from '~/scopes/checkout/components/Cart/components/CartItemCard/components/Insurances/components/InsuranceSelection/InsuranceSelection.vue'
import InsuranceCompliancy from '~/scopes/insurance/components/InsuranceCompliancy/InsuranceCompliancy.vue'
import BackupDrawerFaq from '~/scopes/insurance/components/InsuranceOffers/components/InsuranceProvider/components/BackupDrawerFaq/BackupDrawerFaq.vue'
import BackupPlusDrawerFaq from '~/scopes/insurance/components/InsuranceOffers/components/InsuranceProvider/components/BackupPlusDrawerFaq/BackupPlusDrawerFaq.vue'

import translations from './Insurances.translations'

type Cart = ApiSpecs['/bm/cart']['GET']['response']

const props = defineProps<{
  listingId: number
}>()

const emit = defineEmits<{
  back: []
  loaded: []
  skip: []
  selected: [InsuranceOffer]
  'no-insurances': []
}>()

const experiments = useExperiments()
const $httpFetch = useHttpFetch()
const i18n = useI18n()
const { trackClick } = useTracking()

const selectedOffer = ref<InsuranceOffer | undefined>()
const savedOffer = ref<InsuranceOffer | undefined>()

const isLoading = ref(false)
const isError = ref(false)
const compliancyElement = useTemplateRef<HTMLElement>('compliancyElement')

const newPriceGrid =
  experiments['experiment.serviceFeeGrid'] === 'newServiceFeeGrid'
const includeBackupPlusOffer =
  experiments['experiment.subscriptionsOnCart'] === 'withBackUpPlus'

const { data, status } = useAsyncData(() =>
  $httpFetch('/bm/cart', {
    method: 'GET',
    query: {
      newPriceGrid,
      includeBackupPlusOffer,
    },
  }),
)

const cartItem = computed(() => {
  if (!data.value) {
    return null
  }

  return (
    camelizeKeys<Cart>(data.value)?.cartItems?.find(
      ({ listingId }) => listingId === `${props.listingId}`,
    ) ?? null
  )
})

const shouldShowContent = computed(() => {
  return status.value === 'success' && !isEmpty(cartItem.value?.insuranceOffers)
})

const hasBackupPlusOffers = computed(() => {
  return (
    cartItem.value?.insuranceOffers.some((offer) => offer.isBackupPlus) ?? false
  )
})

watch(
  () => status,
  () => {
    if (status.value === 'error') {
      emit('no-insurances')
      emit('loaded')
    }

    if (status.value === 'success') {
      emit('loaded')
    }
  },
)

watch(cartItem, () => {
  if (cartItem.value && isEmpty(cartItem.value.insuranceOffers)) {
    emit('no-insurances')
  }
})

function addInsurance(offer: InsuranceOffer) {
  isError.value = false
  selectedOffer.value = offer
}

function removeInsurance() {
  isError.value = false
  selectedOffer.value = undefined
}

function toggleCompliancy(offer: InsuranceOffer) {
  isError.value = false
  selectedOffer.value = offer
}

function handleBack() {
  trackClick({
    name: 'back',
    zone: 'insurance_atc',
  })

  emit('back')
}

async function handleSubmit() {
  if (selectedOffer.value) {
    trackClick({
      name: 'add_protection',
      zone: 'insurance_atc',
    })

    try {
      if (
        selectedOffer.value.compliancy.isUserAgreementRequired &&
        !selectedOffer.value.compliancy.isUserAgreementAccepted
      ) {
        if (compliancyElement.value) {
          compliancyElement.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }

        trackClick({
          name: 'continue',
          zone: 'insurance_atc',
          has_compliancy_error: true,
        })

        isError.value = true

        return
      }

      isLoading.value = true

      await $httpFetch('/bm/cart/update_insurance_offer', {
        method: 'POST',
        body: {
          listing_id: `${props.listingId}`,
          insurance_offer_id: `${selectedOffer.value.id}`,
        },
      })

      await $httpFetch('/bm/cart/insurances/agreement', {
        method: 'POST',
        body: {
          listingId: `${props.listingId}`,
          insuranceOfferId: `${selectedOffer.value.id}`,
          isAccepted: selectedOffer.value.compliancy.isUserAgreementAccepted,
        },
      })

      savedOffer.value = selectedOffer.value
      emit('selected', selectedOffer.value)
      isLoading.value = false
    } catch {
      isLoading.value = false
    }
  } else {
    trackClick({
      name: 'skip_protection',
      zone: 'insurance_atc',
    })

    // Remove previously saved offer if we came back from the next step and unselected the offer
    if (savedOffer.value && !selectedOffer.value) {
      await $httpFetch('/bm/cart/update_insurance_offer', {
        method: 'POST',
        body: {
          listing_id: `${props.listingId}`,
          insurance_offer_id: null,
        },
      })

      savedOffer.value = undefined
    }

    emit('skip')
  }
}
</script>
